import { SortingDirection } from 'api/clients/FilesViewsClient'
import { AttributeProperties, DefaultType, Property } from 'api/clients/PropertyClient'
import cx from 'classnames'
import { useProperties } from 'hooks/queries/companies/properties/useProperties'
import { useEffect, useState } from 'react'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Button } from 'components/Button'
import { ReactComponent as ResetIcon } from 'assets/icons/reset16.svg'
import { BackButton } from '../common/BackButton/BackButton'
import { DirectionPicker } from '../common/DirectionPicker/DirectionPicker'
import { PropertyPicker } from '../common/PropertyPicker/PropertyPicker'
import { useFilterProperties } from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/Filter/hooks/useFilterProperties'
import SharedStyles from '../ViewControls.module.scss'
import Styles from './Sort.module.scss'

export const Sort = ({ onBackButtonPress }: { onBackButtonPress?: () => void }) => {
  const { filesView, updateSchema } = useFinderContext()
  useProperties({}, { enabled: true }, { pushToStore: true })
  const [properties] = useFilterProperties({ excludedProperties: [DefaultType.LastOpened, DefaultType.Shared] })

  const defaultProperty = AttributeProperties.name
  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(filesView?.schema.sorting_property)
  const [selectedDirection, setSelectedDirection] = useState<SortingDirection | undefined>(
    filesView?.schema.sorting_direction,
  )

  useEffect(() => {
    setSelectedProperty(filesView?.schema.sorting_property || defaultProperty)
    setSelectedDirection(filesView?.schema.sorting_direction)
  }, [defaultProperty, filesView?.schema.sorting_direction, filesView?.schema.sorting_property])

  const onPropertySelected = (property: Property) => {
    setSelectedProperty(property)
    updateSchema({ ...filesView?.schema, sorting_property_id: property.id })
  }

  const onDirectionSelected = async (direction: SortingDirection) => {
    setSelectedDirection(direction)
    updateSchema({ ...filesView?.schema, sorting_direction: direction })
  }

  const resetSort = async () => {
    setSelectedProperty(defaultProperty)
    setSelectedDirection(SortingDirection.Ascending)
    updateSchema({
      ...filesView?.schema,
      sorting_property_id: defaultProperty.id,
      sorting_direction: SortingDirection.Ascending,
    })
  }

  return (
    <div className={cx(SharedStyles.control, Styles.sortControl)} data-testid="view-modifier-sort">
      <div className={cx(SharedStyles.title, SharedStyles.withAction)}>
        <div className={cx(SharedStyles.left, { [SharedStyles.noBackButton]: !onBackButtonPress })}>
          {!!onBackButtonPress && <BackButton onPress={onBackButtonPress} />} Sort
        </div>
        {(selectedProperty !== defaultProperty || selectedDirection !== SortingDirection.Ascending) && (
          <Tooltip label="Reset to default">
            <div className={SharedStyles.right}>
              <Button
                className={Styles.resetButton}
                variant="tertiary"
                size="md"
                frontIcon={ResetIcon}
                onPress={resetSort}
              />
            </div>
          </Tooltip>
        )}
      </div>
      {selectedProperty && (
        <div className={SharedStyles.content}>
          <div className={Styles.sorting}>
            <PropertyPicker
              selectedProperty={selectedProperty}
              onPropertySelected={onPropertySelected}
              properties={properties}
            />
            <DirectionPicker
              property={selectedProperty}
              selectedDirection={selectedDirection}
              onDirectionSelected={onDirectionSelected}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Sort
